// Which card reader this device uses, remembered in the browser.
//
// Keyed by the shop uid, not the signed-in person: a staff login resolves to
// the owner's user record, so one phone that switches between two staff
// logins keeps its reader, and a browser used for two different shops keeps
// one default per shop. Nothing about this lives in Firestore; Stripe is the
// source of truth for which readers exist, and this is only which one of
// them this device reaches for when Process is pressed.

export interface DeviceReaderChoice {
  serial: string;
  label: string;
  locationId: string | null;
}

const storageKey = (shopUid: string) => `mm:terminal:deviceReader:${shopUid}`;

export const readDeviceReader = (
  shopUid: string | undefined
): DeviceReaderChoice | null => {
  if (!shopUid || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(shopUid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.serial !== "string" || !parsed.serial) {
      return null;
    }
    return {
      serial: parsed.serial,
      label: typeof parsed.label === "string" ? parsed.label : "",
      locationId:
        typeof parsed.locationId === "string" ? parsed.locationId : null,
    };
  } catch {
    return null;
  }
};

export const writeDeviceReader = (
  shopUid: string | undefined,
  choice: DeviceReaderChoice | null
) => {
  if (!shopUid || typeof window === "undefined") return;
  try {
    if (choice) {
      window.localStorage.setItem(storageKey(shopUid), JSON.stringify(choice));
    } else {
      window.localStorage.removeItem(storageKey(shopUid));
    }
  } catch {
    // Storage full or blocked (private mode). The choice just does not stick
    // and the picker shows again next time.
  }
};

// A reader's name as staff see it: every word starts with a capital, so
// "front counter" shows as "Front Counter". The rest of each word is left
// alone ("iPad Reader" stays, "WiFi" stays). Applied when names are saved
// and when they are shown, so readers named before this still display well.
export const formatReaderName = (label: string | null | undefined): string =>
  (label ?? "")
    .trim()
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
